import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { PageHero } from "../components/PageHero";
import {
  Users, Award, Star, Building2,
  Building, CloudCog, Boxes, Server,
  Cloud, Layers as LayersIcon, Sparkles, Network,
  Video, Laptop, Layers,
  Quote
} from "lucide-react";

const IMPACT_STATS = [
  { icon: Users, value: "4000+", label: "Professionals Upskilled", desc: "Professionals trained across enterprise applications, cloud platforms, and digital technologies." },
  { icon: Award, value: "100+", label: "Enterprise Trainers", desc: "Certified instructors delivering hands-on learning across leading technology platforms." },
  { icon: Star, value: "98%", label: "Client Satisfaction", desc: "Organizations continue to choose Skillsgain for high-quality enterprise learning experiences." },
  { icon: Building2, value: "200+", label: "Corporate Learning Programs", desc: "Industry-focused learning aligned with enterprise technology and business objectives." }
];

const LEARNING_OUTCOMES = [
  { icon: Building, title: "SAP Enterprise Applications", desc: "Develop practical expertise in SAP solutions covering finance, supply chain, human capital management, procurement, and business process optimization for enterprise operations." },
  { icon: CloudCog, title: "Oracle Fusion Cloud Applications", desc: "Learn to manage Oracle Fusion Cloud Applications across ERP, HCM, finance, procurement, and customer experience while supporting modern enterprise workflows." },
  { icon: Boxes, title: "Microsoft Dynamics 365", desc: "Gain hands-on experience with Microsoft Dynamics 365 for CRM, ERP, sales, customer service, finance, and intelligent business operations." },
  { icon: Server, title: "Amazon Web Services (AWS)", desc: "Master Amazon Web Services (AWS) fundamentals, cloud architecture, compute, networking, storage, security, and deployment practices for enterprise cloud environments." },
  { icon: Cloud, title: "Microsoft Azure", desc: "Develop expertise in Microsoft Azure services, hybrid cloud infrastructure, identity management, virtualization, networking, and enterprise application hosting." },
  { icon: LayersIcon, title: "Google Cloud Platform (GCP)", desc: "Explore Google Cloud Platform (GCP) services including cloud infrastructure, Kubernetes, data analytics, AI services, application deployment, and cloud operations." },
  { icon: Sparkles, title: "Microsoft Copilot & AI Productivity", desc: "Improve everyday productivity with Microsoft Copilot by streamlining document creation, data analysis, collaboration, workflow automation, and AI-assisted business processes." },
  { icon: Network, title: "Cloud Services & Enterprise Platforms", desc: "Strengthen technical capabilities through Cloud Services Courses covering multi-cloud environments, enterprise platform administration, application integration, cloud governance, and digital business technologies." }
];

const DELIVERY_MODELS = [
  { icon: Video, title: "Live Virtual Training", desc: "Interactive online sessions with expert instructors, platform demonstrations, and guided practical exercises." },
  { icon: Building, title: "Classroom Training", desc: "Instructor-led workshops featuring hands-on labs, enterprise scenarios, and collaborative learning." },
  { icon: Laptop, title: "Self-Paced Learning", desc: "Access recorded sessions, assessments, labs, and learning materials anytime." },
  { icon: Layers, title: "Blended Learning", desc: "A combination of live instruction, self-learning modules, practical assignments, and enterprise projects." }
];

const BY_THE_NUMBERS = [
  { value: "250+", label: "Enterprise Clients" },
  { value: "500+", label: "Instructor-Led Workshops" },
  { value: "25+", label: "Global Technology Partners" },
  { value: "50+", label: "Fortune & High-Growth Companies Served" }
];

const ApplicationsPlatformsPage = () => {
  return (
    <div className="bg-white min-h-screen">
      <Header/>

      <PageHero
        eyebrow="ENTERPRISE APPLICATIONS & PLATFORMS LEARNING"
        title="Enterprise Training for Business Applications, Cloud Platforms & Digital Technologies"
        subtitle="Help your teams gain practical expertise in leading enterprise platforms with Skillsgain's instructor-led learning programs. Explore SAP, Oracle Fusion Cloud Applications, Microsoft Dynamics 365, Amazon Web Services (AWS), Microsoft Azure, Google Cloud Platform (GCP), and Microsoft Copilot through hands-on labs, real-world business scenarios, and enterprise-focused learning."
      />

      {/* Enterprise Learning Impact */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="text-xs font-bold tracking-widest text-[#F57C20] mb-3">ENTERPRISE LEARNING IMPACT</div>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#15348C]">Trusted by organizations for enterprise platform learning</h2>
          </div>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {IMPACT_STATS.map((o, i) => (
            <Reveal key={o.label} variant="up" delay={i * 80}>
              <div className="sg-card bg-white border border-gray-100 rounded-2xl p-6 h-full">
                <div className="w-11 h-11 rounded-lg bg-[#6BBE32]/15 flex items-center justify-center mb-4">
                  <o.icon size={20} className="text-[#6BBE32]"/>
                </div>
                <div className="text-3xl font-bold text-[#15348C]">{o.value}</div>
                <div className="text-sm font-semibold text-[#15348C] mt-1">{o.label}</div>
                <p className="text-xs text-gray-500 mt-2 leading-relaxed">{o.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Applications & Platform Expertise */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="text-xs font-bold tracking-widest text-[#F57C20] mb-3">APPLICATIONS & PLATFORM EXPERTISE</div>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#15348C]">Enterprise learning across leading business applications and cloud platforms</h2>
            <p className="mt-3 text-gray-600">Develop practical expertise with enterprise applications, cloud platforms, productivity tools, and digital technologies through instructor-led sessions, hands-on labs, and real-world business use cases.</p>
          </div>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {LEARNING_OUTCOMES.map((m, i) => (
            <Reveal key={m.title} variant="up" delay={(i % 4) * 80}>
              <div className="sg-card group bg-[#F8F9FA] border border-gray-100 rounded-2xl p-6 h-full">
                <div className="w-11 h-11 rounded-lg bg-[#15348C] flex items-center justify-center mb-4 group-hover:bg-[#6BBE32] transition-colors">
                  <m.icon size={20} className="text-white"/>
                </div>
                <h4 className="font-bold text-[#15348C]">{m.title}</h4>
                <p className="text-sm text-gray-600 mt-2 leading-relaxed">{m.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Learning Experiences */}
      <section className="bg-[#F8F9FA] py-16">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-12">
              <div className="text-xs font-bold tracking-widest text-[#F57C20] mb-3">LEARNING EXPERIENCES</div>
              <h2 className="text-3xl lg:text-4xl font-bold text-[#15348C]">Learning formats designed for enterprise teams</h2>
              <p className="mt-3 text-gray-600">Choose instructor-led virtual learning, classroom workshops, self-paced modules, or blended programs based on your organization's learning objectives and technology roadmap.</p>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {DELIVERY_MODELS.map((m, i) => (
              <Reveal key={m.title} variant="up" delay={i * 80}>
                <div className="flex flex-col gap-3 bg-white border border-gray-100 rounded-xl p-5 h-full sg-card">
                  <div className="w-10 h-10 shrink-0 rounded-lg bg-[#6BBE32]/15 flex items-center justify-center">
                    <m.icon size={18} className="text-[#6BBE32]"/>
                  </div>
                  <div className="font-bold text-[#15348C] text-sm">{m.title}</div>
                  <p className="text-xs text-gray-500 leading-relaxed">{m.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* By the Numbers + Customer Success Story */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid lg:grid-cols-3 gap-8">
          <Reveal variant="up" className="lg:col-span-1">
            <div className="bg-[#15348C] text-white rounded-2xl p-8 h-full relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-48 h-48 bg-[#6BBE32]/20 rounded-full blur-3xl"/>
              <div className="relative">
                <div className="text-xs font-bold tracking-widest text-[#8BD835] mb-3">BY THE NUMBERS</div>
                <div className="space-y-5">
                  {BY_THE_NUMBERS.map(s => (
                    <div key={s.label}>
                      <div className="text-3xl font-bold">{s.value}</div>
                      <div className="text-xs text-gray-300">{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal variant="up" delay={100} className="lg:col-span-2">
            <div className="bg-[#F8F9FA] border border-gray-100 rounded-2xl p-8 h-full flex flex-col">
              <Quote size={32} className="text-[#F57C20] mb-4"/>
              <div className="text-xs font-bold tracking-widest text-[#F57C20] mb-3">CUSTOMER SUCCESS STORY</div>
              <p className="text-lg lg:text-xl text-[#15348C] leading-relaxed">
                Skillsgain delivered a customized enterprise learning program covering SAP, Microsoft Dynamics 365, Microsoft Azure, and Microsoft Copilot. The practical labs and instructor-led sessions helped our teams confidently adopt enterprise applications and cloud technologies across multiple business functions.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-4 pb-20">
        <Reveal variant="scale">
          <div className="bg-[#15348C] rounded-3xl p-10 lg:p-14 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-64 h-64 bg-[#6BBE32]/20 rounded-full blur-3xl"/>
            <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-[#F57C20]/20 rounded-full blur-3xl"/>
            <div className="relative grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="text-xs font-bold tracking-widest text-[#8BD835] mb-3">FOR BUSINESS & TECHNOLOGY LEADERS</div>
                <h2 className="text-3xl lg:text-4xl font-bold text-white leading-tight">Enterprise Learning for Business Applications & Cloud Platforms</h2>
                <p className="mt-3 text-gray-300">Equip your teams with practical expertise in SAP, Oracle Fusion Cloud Applications, Microsoft Dynamics 365, Amazon Web Services, Microsoft Azure, Google Cloud Platform, and Microsoft Copilot through instructor-led enterprise learning.</p>
              </div>
              <div className="flex flex-wrap gap-3 lg:justify-end">
                <Link to="/contact" className="sg-btn-glow bg-[#F57C20] hover:bg-[#D86810] text-white font-semibold px-6 py-3 rounded-md transition-colors">Talk to Our Experts</Link>
                <Link to="/contact" className="border-2 border-white/40 text-white font-semibold px-6 py-3 rounded-md hover:bg-white/10 hover:border-[#8BD835] transition-colors">Request Corporate Proposal</Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      <Footer/>
    </div>
  );
};

export default ApplicationsPlatformsPage;
